/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable security/detect-object-injection */
/* eslint-disable no-underscore-dangle */
import eventBus from '@sd-utility/eventbus';
import React from 'react';
import { createRoot } from 'react-dom/client';

import '@tvg/fuse/styles.css';
import type {
  CIOProductsRec,
  CIOProductsResBodyEntry,
  CIORecProduct,
  CriteoBannerResBodyEntry,
  CriteoProductsResBodyEntry,
} from 'server/services/recommendation';
import {
  EXPERIMENTS,
  isInExperimentControl,
} from 'server/utils/experimentation/clientUtils';
import {
  GRID_UPDATE_EVENT,
  RecPlacement,
  RecProvider,
  RenderType,
  UserAction,
} from 'shared/consts/recommendations';
import { Brand, ConfigProvider } from './context/config';
import InGridProductCardRenderer from './InGridProductCardRenderer';
import { dispatchRexInitiated, dispatchRexLoaded } from './rex-events';
import {
  PageType,
  hydrateAndSubscribeEventBus,
  latestEventBusData,
} from './utils/eventBusUtils';
import {
  EventBusProps,
  hideGridItems,
  shouldUpdateRecs,
} from './utils/gridUpdateEvent';
import isApp from './utils/isApp';
import Mutex from './utils/mutex';

export enum Manufacturer {
  Apple = 'Apple',
}

// Uncomment the line below to publish EventBus mock data for local development
// publishEventBusMockData();

const eventBusEventMap: Record<
  PageType | 'page',
  { getItems?: string; getPage?: string }
> = {
  basket: { getItems: 'eventpulse/view_cart' },
  page: { getPage: 'eventpulse/page_view' },
  plp: {},
  pdp: {},
};

let eventBusHydrated = false;

export const getCurrentPageType = (): PageType | undefined => {
  const { href } = window.location;
  if (href.includes('/basket')) {
    return 'basket';
  }
  if (
    href.includes('/search') ||
    href.includes('/browse') ||
    href.includes('/promo')
  ) {
    return 'plp';
  }

  if (/\/\d+\.prd|\/[a-zA-Z0-9]+\.prd/.test(href)) {
    return 'pdp';
  }
  return undefined;
};

async function ensureEventBusHydrated() {
  const currentPageType = getCurrentPageType();

  if (
    currentPageType === 'basket' &&
    !eventBusHydrated &&
    !window.universal_variable
  ) {
    await hydrateAndSubscribeEventBus(
      eventBusEventMap,
      latestEventBusData,
      eventBus,
    );
    eventBusHydrated = true;
  }
}

ensureEventBusHydrated();

if (!window.__ALL_RECS_QUERIES__) window.__ALL_RECS_QUERIES__ = {};
const brand = window.__RECS_BRAND__ as Brand | undefined;
let isRunning = false;
let lastRun = 0;

const mutex = new Mutex();

export const setIsRunning = (value: boolean) => {
  isRunning = value;
};

export const setLastRun = (value: number) => {
  lastRun = value;
};

// eslint-disable-next-line import/prefer-default-export
export const renderAddToBasket = async (
  rec: CIOProductsRec | null,
  title = '',
) => {
  const containerId = 'recs-atb-container';
  const el = document.getElementById(containerId) as HTMLDivElement | null;
  if (!el || !(rec && rec.productData && rec.productData.length > 0)) return;

  const isCarousel =
    window.universal_variable?.product?.manufacturer === Manufacturer.Apple &&
    rec.productData.length > 1;

  const recProduct = isCarousel
    ? rec.productData.slice(0, 5)
    : rec.productData[0];

  const { AddToBasket } = await import('./components/AddToBasket');
  const { Carousel } = await import('./components/Carousel');

  const callback = () => el.classList.remove('recs-atb--hidden');

  const root = createRoot(el);
  root.render(
    <ConfigProvider
      value={{
        rec,
        provider: RecProvider.Constructor,
        placement: RecPlacement.ATB1,
        brand,
      }}
    >
      {isCarousel ? (
        <Carousel
          lCardsPerPage={2}
          mCardsPerPage={2}
          xxsCardsPerPage={2}
          touchPeek
          products={recProduct as CIORecProduct[]}
          title={title}
          data-testid="recommendations-carousel-add-to-basket"
        />
      ) : (
        <AddToBasket product={recProduct as CIORecProduct} title={title} />
      )}
    </ConfigProvider>,
  );
  callback();
};

export const getUUID = async () => {
  try {
    const useCookies = await isApp();

    if (useCookies) {
      const { setCookie, getCookie } = await import('client/utils/cookies');
      const existingId = getCookie('criteo_visitorID');
      if (existingId) return existingId;

      const { v4: uuidv4 } = await import('uuid');
      const randomID = uuidv4();

      setCookie('criteo_visitorID', randomID, { path: '/' });
      return randomID;
    }

    const sessionModule = await import('client/utils/sessionStorage');
    const { setSession, getSession } = sessionModule;

    const existingId = getSession('criteo_visitorID');
    if (existingId) return existingId;

    const { v4: uuidv4 } = await import('uuid');
    const randomID = uuidv4();

    setSession('criteo_visitorID', randomID);
    return randomID;
  } catch (error) {
    console.error('getUUIDFailed:', error);
    return '';
  }
};

export const getCustomerID = () => window.cnstrcUserId || undefined;

export const getTaxonomyValue = () => {
  const scriptTag = document.querySelector('#taxonomyData');
  if (scriptTag && scriptTag.textContent) {
    try {
      const data = JSON.parse(scriptTag.textContent);
      return data.taxonomy;
    } catch (error) {
      return '';
    }
  }
  return '';
};

const dispatchInitiated = () => {
  window.__RECS_INITIATED__ = true;
  dispatchRexInitiated();
};

export const getAllRoots = () =>
  document.querySelectorAll<HTMLDivElement & { _hydrated?: boolean }>(
    'div#recommendations-root',
  );

export const removeAllRoots = () => getAllRoots().forEach((el) => el.remove());

// Temporary solution to hide sexual wellness products from Recs
export const filterProducts = (productData: CIORecProduct[]) => {
  if (!productData) return [];

  const wellnessProducts = [
    'W12E5',
    'W12E6',
    'W12E7',
    'W12E8',
    'W12E9',
    'W12EA',
    'W12EB',
    'W12EC',
    'W12ED',
    'W12EE',
    'W12EF',
    'W12EG',
    'W12EH',
    'W12EI',
    'W12EJ',
    'W12EK',
    'W12E0',
    'W12E1',
    'W12E2',
    'W12E3',
    'W12E4',
  ];
  return productData.filter(
    (product) =>
      product.cnstrc && !wellnessProducts.includes(product.cnstrc.variationId),
  );
};

interface HydratedDivElement extends HTMLDivElement {
  _hydrated?: boolean;
}

export const handleRender = async (
  entry: CIOProductsResBodyEntry | CriteoProductsResBodyEntry,
  state: any,
  el: HydratedDivElement,
  options: any,
) => {
  // to avoid unnecessarily importing the app if the carousel is just going to disappear
  if (
    !entry.rec ||
    !Array.isArray(entry.rec.productData) ||
    entry.rec.productData.length === 0
  ) {
    el.remove();
    return;
  }

  const { default: App } = await import('./App');
  const root = createRoot(el);
  root.render(
    <App
      config={{
        ...state.config,
        options,
        renderType: entry?.renderType,
        rec: entry?.rec,
        noResponse: !entry?.rec,
        brand,
      }}
    />,
  );
  el._hydrated = true;
  dispatchRexLoaded({
    provider: state.config.provider,
    placement: state.config.placement,
  });
};

export const cleanupInGridRenders = () => {
  const roots = new Map<Element, ReturnType<typeof createRoot>>();
  roots.forEach((root, container) => {
    root.unmount();
    roots.delete(container);
  });
};

const renderInGridRoots = new Map<Element, ReturnType<typeof createRoot>>();

export const renderInGrid = (
  { rec, provider }: CriteoProductsResBodyEntry,
  isSelectedFacet: boolean,
  state: any,
) => {
  // Experiment se-5: InGrid Search
  if (
    isInExperimentControl(
      state.config?.experiments,
      EXPERIMENTS.INGRID_SEARCH,
      'a',
    )
  )
    return;

  const uvListing = window.universal_variable?.listing;
  if (uvListing?.pageType === 'promo') return;
  if (!rec || !rec.productData.length) return;
  try {
    const containers = document.querySelectorAll<HTMLDivElement>(
      'div[data-testid="gallery-recs-container"].grid-item--recs-container',
    );

    /* Don't display inGrid Recs if less than 2/3 products */
    const minGridProducts = window.innerWidth < 768 ? 2 : 3;
    if (rec.productData.length < minGridProducts) {
      console.warn('Not enough products for inGrid:', rec.productData.length);
      return;
    }

    containers.forEach((container, idx) => {
      const product = rec.productData[idx];
      if (!product) return;

      if (!renderInGridRoots.has(container)) {
        const shouldTriggerLoadBeacon = idx === 0;
        renderInGridRoots.set(container, createRoot(container));
        const root = renderInGridRoots.get(container)!;
        root.render(
          <InGridProductCardRenderer
            product={product}
            provider={provider}
            onLoadBeacon={shouldTriggerLoadBeacon ? rec.onLoadBeacon : ''}
            config={{ ...state.config }}
            onRenderComplete={() => {
              setTimeout(() => {
                if (isSelectedFacet) {
                  container.classList.add('grid-item--hidden');
                } else {
                  container.classList.remove('grid-item--hidden');
                }
              }, 50);
            }}
          />,
        );
      } else {
        setTimeout(() => {
          if (container.innerHTML === '' || isSelectedFacet) {
            // Pass value to dataLayer to specify if inGrid displayed?
            container.classList.add('grid-item--hidden');
          } else {
            container.classList.remove('grid-item--hidden');
          }
        }, 300);
      }
    });
  } catch (ex) {
    console.error('Failed to Render inGrid Recs', ex);
  }
};

export const renderBannerInGrid = async (
  entry: CriteoBannerResBodyEntry,
  isSelectedFacet: boolean,
  state: any,
) => {
  const uvListing = window.universal_variable?.listing;
  if (uvListing?.pageType === 'promo') return;
  const bannerEl = document.querySelector<HTMLDivElement>(
    'div[data-testid="gallery-banner-container"].grid-item--recs-banner',
  );
  if (!bannerEl || !entry || !entry.rec) return;

  const { default: BannerContainer } = await import('./BannerContainer');
  const root = createRoot(bannerEl);
  root.render(
    <BannerContainer
      config={{
        ...state.config,
        renderType: entry?.renderType,
        rec: entry?.rec,
        noResponse: !entry?.rec,
        brand,
      }}
    />,
  );
  if (!isSelectedFacet && bannerEl) {
    bannerEl.classList.remove('grid-item--hidden');
  }
};

export const runRecommendations = async (
  fromEventBus = false,
  isSelectedFacet = false,
): Promise<void> => {
  await mutex.lock();
  try {
    await ensureEventBusHydrated();
    if (isRunning || Date.now() - lastRun < 3000) return;
    isRunning = true;
    lastRun = Date.now();

    try {
      const uv = window.universal_variable;
      if (!uv && !eventBus) {
        console.error(new Error('universal_variable_and_EventBus_unavailable'));
        dispatchInitiated();
        return removeAllRoots();
      }

      const { default: Worker } = await import('./rex.worker');
      const worker = new Worker();

      const uvCopy = { ...uv } as UniversalVar;
      delete uvCopy.events;

      const postMessage = async () => {
        const messagePayload = {
          uv: uvCopy,
          qs: window.__ALL_RECS_QUERIES__,
          options: {
            ...window.__GLOBAL_RECS_STATE__,
            screenWidth: window.innerWidth,
            visitorId: await getUUID(),
          },
          customerID: getCustomerID(),
          taxonomy: getTaxonomyValue(),
          eventBusData: latestEventBusData,
          pageType: getCurrentPageType(),
        };
        worker.postMessage(messagePayload);
      };
      worker.onmessage = async ({ data: { options, recs } }) => {
        if (!recs) {
          dispatchInitiated();
          worker.terminate();
          return removeAllRoots();
        }

        document
          .querySelectorAll<
            HTMLDivElement & { _hydrated?: boolean }
          >('div#recommendations-root')
          .forEach((el) => {
            const stateId = el.parentElement?.getAttribute('data-stateid');
            const debugText = 'previously hydrated, skipping hydration for';
            if (!fromEventBus) {
              // * prevents hydrating element multiple times
              if (el._hydrated) return console.debug(debugText, el);
            } else if (!stateId?.includes('recs3')) {
              if (el._hydrated) return console.debug(debugText, el);
            }
            if (!stateId) {
              console.error(new Error('state_id_not_found'));
              return el.remove();
            }
            const state = window.__ALL_RECS_QUERIES__[stateId];
            if (!state) {
              console.error(new Error('state_not_found'));
              return el.remove();
            }
            const res = recs[stateId];
            let inGridRecReturned = false;
            let carouselRecReturned = false;
            let inGridBannerReturned = false;
            if (!res) return el.remove();
            res.forEach((entry) => {
              if (
                entry.provider === 'recs2' &&
                entry.rec &&
                entry.rec.productData
              ) {
                entry.rec.productData = filterProducts(entry.rec.productData);
              }

              switch (entry.renderType) {
                case RenderType.BannerInGrid:
                  inGridBannerReturned = true;
                  return renderBannerInGrid(entry, isSelectedFacet, state);
                case RenderType.InGrid:
                  inGridRecReturned = true;
                  return renderInGrid(entry, isSelectedFacet, state);
                case RenderType.AddToBasket:
                  return renderAddToBasket(entry.rec, entry.rec?.title);
                default:
                  if (entry.provider === 'recs3') {
                    carouselRecReturned = true;
                  }
                  return handleRender(entry as any, state, el, options);
              }
            });
            // PLP hide below grid skeleton if no recs are returned in carousel
            if (
              (inGridRecReturned || inGridBannerReturned) &&
              !carouselRecReturned
            ) {
              el.remove();
            }
          });

        dispatchInitiated();
        worker.terminate();
      };

      await postMessage();
    } finally {
      isRunning = false;
    }
  } finally {
    mutex.unlock();
  }
};

if (!window.__RECS_INITIATING__) {
  window.__RECS_INITIATING__ = true;
  const isSelectedFacet =
    !!window.universal_variable?.listing?.filters?.numSelections;

  runRecommendations(false, isSelectedFacet);
}

// istanbul ignore next
if (module.hot) {
  module.hot.accept((error) => error && console.error(error));
}

const subscription = eventBus.subscribe(
  GRID_UPDATE_EVENT,
  (e: EventBusProps) => {
    if (shouldUpdateRecs(e)) {
      runRecommendations(true, e?.isSelectedFacet);
    } else if (e?.eventTrigger === UserAction.FACET && e?.isSelectedFacet) {
      hideGridItems();
    }
  },
);

window.addEventListener('beforeunload', () => subscription.unsubscribe(), {
  once: true,
});
